/**
 * MaestroChatPanel payload parsers (PR-D2 decomposition).
 *
 * Pure functions extracted from the original 1,577-line MaestroChatPanel.tsx
 * per gist finding I19. These have no React or DOM dependencies and are
 * unit-testable in isolation.
 */

import type {
  ApprovalRequiredEvent,
  ChatModelOption,
  LlmGenerateResponse,
  ModelOutput,
} from '../../../../types/chat';

/** Pick the first non-empty model output; fall back to outputs[0]. */
export const pickPrimaryModelOutput = (response: LlmGenerateResponse): ModelOutput | undefined => {
  const outputs = response.model_outputs ?? [];
  if (!outputs.length) return undefined;
  return outputs.find((item) => item.content?.trim()) ?? outputs[0];
};

/** Resolve a friendly label for a given model key / id from the option list. */
export const getModelDisplayName = (modelKey: string, options: ChatModelOption[]): string => {
  const found = options.find((item) => item.key === modelKey || item.model_id === modelKey);
  return found?.label || modelKey;
};

/**
 * Drop only known-noise lines from streamed orchestrator trace ("CoT") output;
 * keep everything else raw so the UI shows what the agent emitted.
 */
export function sanitizeCoTDisplay(raw: string): string {
  if (!raw.trim()) return '';
  return raw
    .split('\n')
    .filter((line) => {
      const t = line.trim();
      if (!t) return true;
      if (t.startsWith('{') && /"raw"\s*:|'raw'\s*:/.test(t)) return false;
      if (/empty_tool_content/i.test(t)) return false;
      return true;
    })
    .join('\n');
}

/**
 * Guard that prevents a ui_action bundle replay from re-applying an action the
 * client has already processed. Returns true and marks the id as seen on the
 * first call; returns false for any subsequent call with the same id.
 *
 * Actions without an action_id (legacy / backward-compat) always pass through.
 */
export function consumeUiAction(action: { action_id?: string }, seenIds: Set<string>): boolean {
  const id = action.action_id;
  if (!id) return true;
  if (seenIds.has(id)) return false;
  seenIds.add(id);
  return true;
}

/**
 * Normalize SSE `approval_required` payloads so the `mapping` and `user_input`
 * branches survive JSON parse with correct shapes for the rest of the UI.
 */
export function parseApprovalRequiredPayload(raw: Record<string, unknown>): ApprovalRequiredEvent {
  const mapPairs = (x: unknown) => {
    if (!Array.isArray(x)) return undefined;
    const out: Array<{ label: string; value: unknown }> = [];
    for (const row of x) {
      if (!row || typeof row !== 'object') continue;
      const o = row as Record<string, unknown>;
      if (o.value === undefined || o.value === null) continue;
      out.push({ label: String(o.label ?? o.value ?? ''), value: o.value });
    }
    return out.length ? out : undefined;
  };

  const mm = raw.mapping;
  let mapping: ApprovalRequiredEvent['mapping'] | undefined;
  if (mm && typeof mm === 'object' && mm !== null) {
    const m = mm as Record<string, unknown>;
    mapping = {
      fd_key: String(m.fd_key ?? ''),
      field_label: m.field_label != null ? String(m.field_label) : undefined,
      proposed_label: String(m.proposed_label ?? ''),
      proposed_value: m.proposed_value,
      alternatives: mapPairs(m.alternatives),
      options: mapPairs(m.options),
      brief_term: m.brief_term != null ? String(m.brief_term) : undefined,
      explanation: m.explanation != null ? String(m.explanation) : undefined,
    };
  }

  const ui = raw.user_input;
  let user_input: ApprovalRequiredEvent['user_input'] | undefined;
  if (ui && typeof ui === 'object') {
    const u = ui as Record<string, unknown>;
    const ft = u.field_type;
    user_input = {
      field: String(u.field ?? ''),
      question: String(u.question ?? ''),
      field_type: ft === 'date' || ft === 'number' || ft === 'text' ? ft : undefined,
      fd_key: u.fd_key != null ? String(u.fd_key) : undefined,
      context: u.context != null ? String(u.context) : undefined,
    };
  }

  return {
    run_id: String(raw.run_id ?? ''),
    approval_phase: String(raw.approval_phase ?? ''),
    confidence: typeof raw.confidence === 'number' ? raw.confidence : 0,
    issues: Array.isArray(raw.issues) ? raw.issues.map((item) => String(item)) : [],
    output_snapshot: String(raw.output_snapshot ?? ''),
    detail: String(raw.detail ?? ''),
    mapping,
    user_input,
  };
}
